"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { MagneticButton } from "@/components/custom/magnetic_button";
import { Input } from "@/components/ui/input";
import { type FormSchemaType, formSchema } from "@/lib/form_schema";
import { submitForm } from "@/lib/form_submit";

interface BlogNewsletterFormProps {
	source: string;
}

export function BlogNewsletterForm({ source }: BlogNewsletterFormProps) {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const defaultMessage = `Newsletter subscription from ${source}`;

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<FormSchemaType>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			Email: "",
			Message: defaultMessage,
		},
	});

	const onSubmit = async (data: FormSchemaType) => {
		setIsSubmitting(true);
		try {
			const result = await submitForm({
				...data,
				Message: defaultMessage,
			});

			if (result.success) {
				toast.success("Subscribed successfully.");
				reset({ Email: "", Message: defaultMessage });
			} else {
				toast.error(result.error || "Subscription failed. Please try again.");
			}
		} catch {
			toast.error("Subscription failed. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<form className="max-w-lg mx-auto" onSubmit={handleSubmit(onSubmit)}>
			<div className="flex flex-col sm:flex-row gap-4">
				<Input
					type="email"
					placeholder="Enter your email address"
					required
					aria-invalid={Boolean(errors.Email)}
					className="h-14 bg-white/5 border-white/10 text-white placeholder:text-zinc-500 rounded-xl focus:border-primary focus:ring-primary/20 transition-all font-inter"
					{...register("Email")}
				/>
				<MagneticButton
					type="submit"
					disabled={isSubmitting}
					className="sm:w-auto w-full disabled:opacity-70 disabled:cursor-not-allowed"
				>
					<div className="h-14 px-8 bg-primary text-white rounded-xl font-syne font-bold uppercase tracking-wide flex items-center justify-center hover:bg-primary/90 transition-colors whitespace-nowrap">
						{isSubmitting ? (
							<>
								<Loader2 className="w-4 h-4 mr-2 animate-spin" />
								Subscribing
							</>
						) : (
							<>
								Subscribe Now
								<ArrowRight className="w-4 h-4 ml-2" />
							</>
						)}
					</div>
				</MagneticButton>
			</div>
			{errors.Email && (
				<p className="mt-2 text-xs text-red-300 font-inter">
					{errors.Email.message}
				</p>
			)}
		</form>
	);
}
